// =====================================================
// client/src/components/event/TaskAdd.jsx
// =====================================================
// A comprehensive task management component for creating and 
// updating tasks within events. Users can assign tasks to 
// tenant users, set priorities, due dates, and add mentions.
// =====================================================

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { FaTimes, FaSave, FaPlus, FaTrash, FaTasks, FaArrowLeft } from "react-icons/fa";
import { getSessionLocal, sessionRequestConfig } from "../../services/sessionLocal";
import CommentBox from "./CommentBox";
import AppFooter from "../AppFooter";
import { notifyTaskMentions } from "../../services/messagesApi";
import TimeSelect from '../TimeSelect';
import { localToUTC } from '../../utils/timezoneUtils';

// =====================================================
// Helper Functions
// =====================================================

/**
 * Extract @mentions from text
 * @param {string} text - Text to parse for mentions
 * @returns {string[]} Array of unique usernames (without @)
 */
const extractMentions = (text) => {
  if (!text) return [];
  const mentionRegex = /@(\w+)/g;
  const mentions = [];
  let match;
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[1]);
  }
  return [...new Set(mentions)]; // Remove duplicates
};

/**
 * Get default due date (2 days from now)
 * @returns {string} Date string in YYYY-MM-DD format
 */
const getDefaultDueDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 2);
  return date.toISOString().split("T")[0];
};

/**
 * Get default due time (current time rounded to next hour)
 * @returns {string} Time string in HH:MM format
 */
const getDefaultDueTime = () => {
  const date = new Date();
  date.setHours(date.getHours() + 1, 0, 0, 0);
  return date.toTimeString().slice(0, 5);
};

/**
 * Format date for display
 * @param {string} dateStr - Date string to format
 * @returns {string} Formatted date string
 */
const formatDate = (dateStr) => {
  if (!dateStr) return "No date";
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
};

/**
 * Get priority color class for styling
 * @param {string} priority - Priority level
 * @returns {string} CSS class name
 */
const getPriorityColor = (priority) => {
  const colors = {
    low: "#6b7280",      // gray
    medium: "#3b82f6",   // blue
    high: "#f59e0b",     // amber
    urgent: "#ef4444",   // red
  };
  return colors[priority] || colors.medium;
};

/**
 * Get status badge style
 * @param {string} status - Task status
 * @returns {object} Style object for badge
 */
const getStatusStyle = (status) => {
  const styles = {
    pending: { bg: "#fef3c7", color: "#92400e" },
    "in-progress": { bg: "#dbeafe", color: "#1e40af" },
    completed: { bg: "#d1fae5", color: "#065f46" },
    blocked: { bg: "#fee2e2", color: "#991b1b" },
  };
  return styles[status] || styles.pending;
};

// =====================================================
// Main Component
// =====================================================

const TaskAdd = ({ tenantId: propTenantId }) => {
  // Router hooks
  const { tenant, eventId, taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine tenant ID from props or URL params
  const tenantId = propTenantId || tenant;
  
  // Query params for item context
  const queryParams = new URLSearchParams(location.search);
  const itemId = queryParams.get("itemId");
  
  // Base URL for API calls
  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  
  // =====================================================
  // State Management
  // =====================================================
  
  // Event/Item context
  const [event, setEvent] = useState(null);
  const [eventName, setEventName] = useState("");
  const [itemName, setItemName] = useState("");
  
  // Existing tasks list
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("pending");
  const [priority, setPriority] = useState("medium");
  const [assignedTo, setAssignedTo] = useState(null);
  const [dueDate, setDueDate] = useState(getDefaultDueDate());
  const [dueTime, setDueTime] = useState('09:00');
  const [tenantTimezone, setTenantTimezone] = useState('America/Chicago');
  const [comment, setComment] = useState("");
  
  // Users for assignment dropdown
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Get current user info for createdBy
  const session = getSessionLocal();
  const currentUsername = session?.username || localStorage.getItem("username") || "";

  useEffect(() => {
    const fetchTenantTimezone = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/api/tenants/${tenantId}/timezone`,
          sessionRequestConfig()
        );
        if (response.data.timezone) {
          setTenantTimezone(response.data.timezone);
        }
      } catch (err) {
        console.error('Failed to fetch tenant timezone:', err);
      }
    };
    
    if (tenantId) {
      fetchTenantTimezone();
    }
  }, [tenantId, baseUrl]);

  // =====================================================
  // Dropdown Options
  // =====================================================
  
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "in-progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "blocked", label: "Blocked" },
  ];

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ];
  
  // Custom styles for react-select to improve readability
  const customSelectStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#0066cc'  // Dark blue for selected
        : state.isFocused 
          ? '#e6f2ff'  // Light blue for hover/focus
          : '#fff',    // White for default
      color: state.isSelected ? '#fff' : '#000',  // White text when selected, black otherwise
      cursor: 'pointer',
      padding: '8px 12px',
      ':active': {
        backgroundColor: '#0066cc',
        color: '#fff'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      zIndex: 9999
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      padding: 0
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      borderColor: '#ced4da',
      minHeight: '38px'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#000'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d'
    })
  };
  
  // Transform users for react-select
  const userOptions = users.map((user) => ({
    value: user.id || user.userid || user.userId,
    label: user.username || user.displayName || user.email || `User ${user.id}`,
  }));

  // =====================================================
  // API Functions
  // =====================================================
  
  /**
   * Fetch event details for header display
   */
  const fetchEvent = useCallback(async () => {
    if (!eventId || !tenantId) return;
    
    try {
      const cfg = sessionRequestConfig();
      const { data } = await axios.get(
        `${baseUrl}/api/events/${tenantId}/${eventId}`,
        cfg
      );
      const eventData = data?.event || data || {};
      setEvent(eventData);
      // setEventName(eventData.name || eventData.title || "");
      setEventName(eventData.title || "");
    } catch (err) {
      console.error("Error fetching event:", err);
      // Try alternative endpoint
      try {
        const cfg = sessionRequestConfig();
        const { data } = await axios.get(
          `${baseUrl}/api/events/${eventId}`,
          cfg
        );
        setEvent(data);
        // setEventName(data?.name || data?.title || "");
        setEventName(data?.title || "");
      } catch (err2) {
        console.error("Error fetching event (fallback):", err2);
      }
    }
  }, [eventId, tenantId, baseUrl]);

  /**
   * Fetch item details if itemId is provided
   */
  const fetchItem = useCallback(async () => {
    if (!itemId || !eventId || !tenantId) return;
    
    try {
      const cfg = sessionRequestConfig();
      const { data } = await axios.get(
        `${baseUrl}/api/events/${tenantId}/${eventId}/items/${itemId}`,
        cfg
      );
      setItemName(data?.name || data?.title || "");
    } catch (err) {
      console.error("Error fetching item:", err);
    }
  }, [itemId, eventId, tenantId, baseUrl]);

  /**
   * Fetch tenant users for assignment dropdown
   */
  const fetchUsers = useCallback(async () => {
    if (!tenantId) {
      console.warn("fetchUsers: No tenantId available");
      return;
    }

    setUsersLoading(true);
    
    try {
      const cfg = sessionRequestConfig();
      
      // Try multiple endpoints
      const endpoints = [
        `/api/users/${tenantId}`,
        `/api/tenants/${tenantId}/users`,
      ];

      let response = null;
      
      for (const endpoint of endpoints) {
        try {
          response = await axios.get(`${baseUrl}${endpoint}`, cfg);
          break;
        } catch {
          continue;
        }
      }

      if (!response) {
        console.warn("No user endpoints worked");
        setUsers([]);
        return;
      }

      // Parse response - handle various formats
      let usersList = [];
      const data = response.data;

      if (Array.isArray(data)) {
        usersList = data;
      } else if (data?.users && Array.isArray(data.users)) {
        usersList = data.users;
      } else if (data?.data && Array.isArray(data.data)) {
        usersList = data.data;
      }

      // Filter valid users
      const validUsers = usersList.filter(
        (user) =>
          user &&
          (user.id || user.userid || user.userId) &&
          (user.username || user.email || user.displayName)
      );

      setUsers(validUsers);
    } catch (err) {
      console.error("Error fetching users:", err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [tenantId, baseUrl]);

  /**
   * Fetch existing tasks for this event
   */
  const fetchTasks = useCallback(async () => {
    if (!eventId || !tenantId) return;
    
    try {
      const cfg = sessionRequestConfig();
      const params = itemId ? `?eventItemId=${itemId}` : "";
      const { data } = await axios.get(
        `${baseUrl}/api/events/${tenantId}/${eventId}/tasks${params}`,
        cfg
      );
      
      // Normalize task data
      let taskList = data?.tasks || [];
      if (Array.isArray(taskList[0]) && !taskList[0]?.id) {
        taskList = taskList[0]; // Handle MySQL [rows, fields] format
      }
      
      setTasks(taskList.filter((t) => t && t.id));
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setTasks([]);
    }
  }, [eventId, tenantId, itemId, baseUrl]);

  // =====================================================
  // Effect Hooks
  // =====================================================
  
  // Initial data fetch
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      await Promise.all([
        fetchEvent(),
        fetchItem(),
        fetchUsers(),
        fetchTasks(),
      ]);
      setLoading(false);
    };
    
    if (tenantId && eventId) {
      initialize();
    }
  }, [tenantId, eventId, fetchEvent, fetchItem, fetchUsers, fetchTasks]);

  // Load task data if editing
  useEffect(() => {
    if (taskId && tasks.length > 0) {
      const task = tasks.find((t) => String(t.id) === String(taskId));
      if (task) {
        setSelectedTask(task);
        setTitle(task.title || "");
        setDescription(task.description || task.comment || "");
        setStatus(task.status || "pending");
        setPriority(task.priority || "medium");
        setDueDate(task.taskdate || task.date || getDefaultDueDate());
        setDueTime(task.tasktime || task.time || getDefaultDueTime());
        setComment(task.comment || "");
        
        // Set assigned user
        if (task.assignedto) {
          const user = users.find((u) => u.id === task.assignedto);
          if (user) {
            setAssignedTo({
              value: user.id,
              label: user.username || user.email,
            });
          }
        }
      }
    }
  }, [taskId, tasks, users]);

  // =====================================================
  // Form Handlers
  // =====================================================
  
  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setTitle("");
    setDescription("");
    setStatus("pending");
    setPriority("medium");
    setAssignedTo(null);
    setDueDate(getDefaultDueDate());
    setDueTime(getDefaultDueTime());
    setComment("");
    setSelectedTask(null);
    setError("");
    setSuccess("");
  };

  /**
   * Handle form submission (create or update)
   */
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Validation
    if (!title.trim()) {
      setError("Task title is required");
      return;
    }
    
    if (!currentUsername) {
      setError("User not authenticated. Please log in again.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const cfg = sessionRequestConfig();
      
      // Convert local time to UTC
      let dueDateTimeUTC = null;
      if (dueDate && dueTime) {
        try {
          dueDateTimeUTC = localToUTC(dueDate, dueTime, tenantTimezone);
        } catch (err) {
          console.error('Error converting time to UTC:', err);
        }
      }
      
      // Build task payload - use 'title' to match backend expectation
      const taskData = {
        title: title.trim(),
        description: description.trim(),
        comment: comment.trim(),
        status,
        priority,
        assignedto: assignedTo?.value || null,
        assignedby: currentUsername,
        taskdate: dueDate || null,
        tasktime: dueTime || null,
        due_datetime_utc: dueDateTimeUTC,
        eventitemid: itemId || null,
        createdby: selectedTask ? selectedTask.createdby : currentUsername,
      };

      let result;
      
      if (selectedTask) {
        // Update existing task
        result = await axios.put(
          `${baseUrl}/api/events/${tenantId}/${eventId}/tasks/${selectedTask.id}`,
          taskData,
          cfg
        );
      } else {
        // Create new task
        result = await axios.post(
          `${baseUrl}/api/events/${tenantId}/${eventId}/tasks`,
          taskData,
          cfg
        );
      }

      // Handle mentions if comment has any
      const mentions = extractMentions(comment);
      if (mentions.length > 0) {
        try {
          await notifyTaskMentions({
            taskId: result.data.task?.id || selectedTask?.id,
            tenantId,
            eventId,
            itemId,
            mentions,
            message: comment,
            createdBy: currentUsername,
          }, cfg);
        } catch (mentionErr) {
          console.error("Failed to send mention notifications:", mentionErr);
          // Don't fail the entire operation if mentions fail
        }
      }

      // Success
      const successMsg = selectedTask
        ? "Task updated successfully!"
        : "Task created successfully!";
      setSuccess(successMsg);

      // Trigger bell icon refresh so new task messages appear immediately
      window.dispatchEvent(new Event('new-message'));

      // Refresh task list
      await fetchTasks();
      
      // Reset form if creating new task
      if (!selectedTask) {
        resetForm();
      }
      
      // Auto-hide success message
      setTimeout(() => setSuccess(""), 3000);
      
    } catch (err) {
      console.error("Error saving task:", err);
      setError(
        err.response?.data?.message || 
        err.message || 
        "Failed to save task. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle task deletion
   */
  const handleDelete = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }

    try {
      const cfg = sessionRequestConfig();
      await axios.delete(
        `${baseUrl}/api/events/${tenantId}/${eventId}/tasks/${taskId}`,
        cfg
      );
      
      setSuccess("Task deleted successfully!");
      await fetchTasks();
      
      if (selectedTask?.id === taskId) {
        resetForm();
      }
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting task:", err);
      setError(err.response?.data?.message || "Failed to delete task");
    }
  };

  /**
   * Handle task selection for editing
   */
  const handleSelectTask = (task) => {
    setSelectedTask(task);
    setTitle(task.title || "");
    setDescription(task.description || task.comment || "");
    setStatus(task.status || "pending");
    setPriority(task.priority || "medium");
    setDueDate(task.taskdate || task.date || getDefaultDueDate());
    setDueTime(task.tasktime || task.time || getDefaultDueTime());
    setComment(task.comment || "");
    
    // Set assigned user
    if (task.assignedto) {
      const user = users.find((u) => u.id === task.assignedto);
      if (user) {
        setAssignedTo({
          value: user.id,
          label: user.username || user.email,
        });
      }
    } else {
      setAssignedTo(null);
    }
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Handle cancel/back action
   */
  const handleCancel = () => {
    if (selectedTask) {
      resetForm();
    } else {
      navigate(-1);
    }
  };

  // =====================================================
  // Render
  // =====================================================
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ paddingBottom: "100px" }}>
      {/* ===== Header (matching EventDetail style) ===== */}
      <div
        className="event-header"
        style={{
          background: "linear-gradient(135deg, #01057a 0%, #1e40af 100%)",
          color: "white",
          padding: "20px 16px",
          margin: "-12px -12px 0",
          borderRadius: "0 0 16px 16px",
        }}
      >
        <div className="d-flex align-items-center gap-2 mb-2">
          <FaTasks size={24} />
          <h1 className="h4 mb-0">Tasks</h1>
        </div>
        
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" style={{ opacity: 0.9 }}>
          <ol className="breadcrumb mb-0" style={{ fontSize: "0.9rem" }}>
            <li className="breadcrumb-item text-white-50">
              {eventName || `Event #${eventId}`}
            </li>
            {itemName && (
              <li className="breadcrumb-item text-white-50">{itemName}</li>
            )}
            <li className="breadcrumb-item active text-white">
              {selectedTask ? "Edit Task" : "New Task"}
            </li>
          </ol>
        </nav>
      </div>

      {/* ===== Alerts ===== */}
      <div className="mt-3 px-2">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError("")}
              aria-label="Close"
            />
          </div>
        )}
        
        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {success}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccess("")}
              aria-label="Close"
            />
          </div>
        )}
      </div>

      {/* ===== Existing Tasks List ===== */}
      <div className="px-2 mt-3">
        <h6 className="text-muted mb-2">
          Existing Tasks ({tasks.length})
        </h6>
        
        <div
          className="task-list"
          style={{
            maxHeight: "200px",
            overflowY: "auto",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            backgroundColor: "#f9fafb",
          }}
        >
          {tasks.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <FaTasks size={24} className="mb-2" style={{ opacity: 0.5 }} />
              <p className="mb-0">No tasks yet. Create one below!</p>
            </div>
          ) : (
            tasks.map((task) => {
              const statusStyle = getStatusStyle(task.status);
              return (
                <div
                  key={task.id}
                  className={`task-item p-3 border-bottom ${
                    selectedTask?.id === task.id ? "bg-primary bg-opacity-10" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSelectTask(task)}
                >
                  {/* Row 1: Title + Status + Priority */}
                  <div className="d-flex align-items-center" style={{ gap: 6 }}>
                    <strong style={{ color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.title}</strong>
                    <span
                      style={{
                        fontSize: "0.65rem",
                        padding: "1px 4px",
                        borderRadius: "4px",
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.color,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {task.status || "pending"}
                    </span>
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: getPriorityColor(task.priority),
                        flexShrink: 0,
                      }}
                      title={`Priority: ${task.priority || "medium"}`}
                    />
                  </div>
                  {/* Row 2: Date info + Delete button */}
                  <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 0 }}>
                    <small className="text-muted" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {formatDate(task.taskdate || task.date)}
                      {task.assignedto && ` • Assigned`}
                    </small>
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{
                        color: "#dc3545",
                        border: "1px solid #dc3545",
                        padding: "1px 4px",
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(task.id);
                      }}
                      title="Delete task"
                    >
                      <FaTrash size={10} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {selectedTask && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={resetForm}
          >
            <FaPlus className="me-1" /> New Task
          </button>
        )}
      </div>

      {/* ===== Task Form ===== */}
      <form onSubmit={handleSubmit} className="px-2 mt-4">
        <h6 className="text-muted mb-3">
          {selectedTask ? "Edit Task" : "Create New Task"}
        </h6>

        {/* Title */}
        <div className="mb-3">
          <label htmlFor="taskTitle" className="form-label">
            Title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="taskTitle"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter task title"
            disabled={saving}
            required
          />
        </div>

        {/* Description */}
        <div className="mb-3">
          <label htmlFor="taskDescription" className="form-label">
            Description
          </label>
          <textarea
            id="taskDescription"
            className="form-control"
            rows="2"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter task description"
            disabled={saving}
          />
        </div>

        {/* Status & Priority Row */}
        <div className="row">
          <div className="col-6 mb-3">
            <label className="form-label">Status</label>
            <Select
              options={statusOptions}
              value={statusOptions.find((opt) => opt.value === status)}
              onChange={(selected) => setStatus(selected?.value || "pending")}
              isDisabled={saving}
              isSearchable={false}
              styles={customSelectStyles}
            />
          </div>
          <div className="col-6 mb-3">
            <label className="form-label">Priority</label>
            <Select
              options={priorityOptions}
              value={priorityOptions.find((opt) => opt.value === priority)}
              onChange={(selected) => setPriority(selected?.value || "medium")}
              isDisabled={saving}
              isSearchable={false}
              styles={customSelectStyles}
            />
          </div>
        </div>

        {/* Assign To & Due Date Row */}
        <div className="row">
          <div className="col-6 mb-3">
            <label className="form-label">Assign To</label>
            {usersLoading ? (
              <div className="form-control d-flex align-items-center">
                <span className="spinner-border spinner-border-sm me-2" />
                Loading...
              </div>
            ) : (
              <Select
                options={userOptions}
                value={assignedTo}
                onChange={setAssignedTo}
                placeholder="Select user"
                isClearable
                isSearchable
                isDisabled={saving}
                noOptionsMessage={() => "No users found"}
                styles={customSelectStyles}
              />
            )}
          </div>
          <div className="col-6 mb-3">
            <label htmlFor="taskDueDate" className="form-label">
              Due Date
            </label>
            <input
              type="date"
              id="taskDueDate"
              className="form-control"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              disabled={saving}
            />
          </div>
        </div>

        {/* Due Time */}
        <div className="mb-3">
          <label htmlFor="taskDueTime" className="form-label">
            Due Time
          </label>
          <TimeSelect
            value={dueTime}
            onChange={setDueTime}
            tenantTimezone={tenantTimezone}
            showTimezone={true}
            disabled={saving}
            id="task-due-time"
          />
          <small className="text-muted d-block mt-1">
            ⏰ Reminder sent 1 hour before due time
          </small>
        </div>

        {/* Comment with @mentions */}
        <div className="mb-3">
          <label className="form-label">Comment</label>
          <CommentBox
            value={comment}
            onChange={(e) => setComment(e?.target?.value || e)}
            onCommentChange={setComment}
            placeholder="Add a comment... Use @ to mention users"
            rows={3}
          />
          {comment && extractMentions(comment).length > 0 && (
            <small className="text-info d-block mt-1">
              Will notify: {extractMentions(comment).join(", ")}
            </small>
          )}
        </div>
      </form>

      {/* ===== Footer with action buttons (using AppFooter) ===== */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: handleCancel,
            disabled: saving,
          },
          {
            icon: selectedTask ? <FaSave /> : <FaPlus />,
            label: selectedTask ? "Update" : "Create",
            onClick: handleSubmit,
            disabled: saving || !title.trim(),
          },
        ]}
      />
    </div>
  );
};

export default TaskAdd;